import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { PanelComponent } from '@components/core/panel/panel.component';
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectComponent } from "@ng-select/ng-select";
import { ActivatedRoute } from '@angular/router';
import Swal from 'sweetalert2';
import { UserService } from '@services/core/user.service';
import { ModuleService } from '@services/core/module.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { stateOfChildren, buildBulkItems, setChildrenEnabled } from './menus-tristate.utils';

@Component({
  selector: 'app-user-menus',
  templateUrl: './menus.component.html',
  styleUrls: ["./menus.component.scss"],
  imports: [FormsModule, ReactiveFormsModule, NgbTooltipModule, PanelComponent, NgSelectComponent],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
})
export class MenusComponent implements OnInit {

  private modalService = inject(NgbModal)
  private _fb = inject(FormBuilder)
  private _userService = inject(UserService)
  private _moduleService = inject(ModuleService)
  private route = inject(ActivatedRoute)
  private destroyRef = inject(DestroyRef)
  private modalOptions = {
    size: 'xl',
    scrollable: true,
    animation: true,
    backdrop: null
  }
  public closeResult = ""

  protected menuForm = this._fb.group({
    id: [null],
    title: [null, Validators.required],
    icon: [null],
    url: [null],
    parentId: [null],
    moduleId: [null],
  })

  protected roles: any[] = [];
  protected roleSelected: string | null = null
  protected menus: any[] = []
  protected modulos: any[] = []

  ngOnInit(): void {
    const data = this.route.snapshot.data['menusData']
    this.roles = data.roles
    this.roleSelected = data.roleSelected
    this.menus = data.menus
    this.loadModulos()
  }

  loadModulos() {
    this._moduleService.getActiveModules().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.modulos = res
    })
  }

  onRoleChange() {
    if (this.roleSelected) {
      this.loadMenusByRole()
    }
  }

  loadMenusByRole() {
    this._userService.getMenuByRole(this.roleSelected!).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.menus = res
    })
  }

  open(modal: any, menu?: any) {
    if (menu) this.menuForm.patchValue(menu)
    else this.menuForm.reset()
    this.modalService.open(modal, this.modalOptions).result.then((result) => {
      this.closeResult = `Closed with: ${result}`;
    }, () => {});
  }

  saveMenu() {
    if (this.menuForm.valid) {
      this._userService.updateMenu(this.menuForm.value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        if (this.roleSelected) this.loadMenusByRole()
        this.menuForm.reset()
      })
    }
  }

  toggleMenuRole(menu: any, enabled: boolean) {
    this._userService.toggleMenuRole(this.roleSelected!, menu.id, enabled).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      menu.enabled = enabled
    })
  }

  childState(menu: any): string {
    return stateOfChildren(menu.submenu);
  }

  toggleParent(menu: any) {
    const enabled = this.childState(menu) !== 'all';
    const items = buildBulkItems(this.roleSelected!, menu, enabled);
    this._userService.toggleMenuRoleBulk(items).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.applyBulkLocal(menu, enabled),
      error: (err) => this.failBulk(err),
    });
  }

  private applyBulkLocal(menu: any, enabled: boolean) {
    menu.enabled = enabled;
    setChildrenEnabled(menu.submenu, enabled);
  }

  private failBulk(err: any) {
    Swal.fire({ icon: 'error', title: 'Error', text: err?.error?.error || 'Error al actualizar menús' });
  }

  moveUp(menu: any, index: number) {
    if (index === 0) return
    this.menus[index] = this.menus[index - 1]
    this.menus[index - 1] = menu
    this.saveOrder()
  }

  moveDown(menu: any, index: number) {
    if (index === this.menus.length - 1) return
    this.menus[index] = this.menus[index + 1]
    this.menus[index + 1] = menu
    this.saveOrder()
  }

  moveSubUp(parent: any, sub: any, index: number) {
    if (index === 0) return
    parent.submenu[index] = parent.submenu[index - 1]
    parent.submenu[index - 1] = sub
    this.saveOrder()
  }

  moveSubDown(parent: any, sub: any, index: number) {
    if (index === parent.submenu.length - 1) return
    parent.submenu[index] = parent.submenu[index + 1]
    parent.submenu[index + 1] = sub
    this.saveOrder()
  }

  private saveOrder() {
    const payload = buildMenuOrderPayload(this.menus);
    this._userService.reorderMenus(payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.roleSelected) this.loadMenusByRole();
    });
  }

  askRemoveMenu(menu: any) {
    Swal.fire({ icon: 'warning', title: 'Eliminar menú', text: `¿Eliminar "${menu.title}"?`, showCancelButton: true, confirmButtonText: 'Eliminar' }).then((r) => {
      if (r.isConfirmed) this.removeMenu(menu);
    });
  }

  removeMenu(menu: any) {
    this._userService.deleteMenu(menu.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.dropMenu(menu.id),
      error: (err) => this.failRemove(err),
    });
  }

  private dropMenu(menuId: string) {
    this.menus = dropById(this.menus, menuId);
  }

  private failRemove(err: any) {
    Swal.fire({ icon: 'error', title: 'Error', text: removeErrorText(err) });
  }
}

function dropById(menus: any[], menuId: string): any[] {
  const out: any[] = [];
  for (const menu of menus) pushKeptMenu(out, menu, menuId);
  return out;
}

function pushKeptMenu(out: any[], menu: any, menuId: string): void {
  if (menu.id === menuId) return;
  menu.submenu = keepSubs(menu.submenu, menuId);
  out.push(menu);
}

function keepSubs(submenu: any[], menuId: string): any[] {
  const out: any[] = [];
  for (const sub of (submenu || [])) pushKept(out, sub, menuId);
  return out;
}

function pushKept(out: any[], sub: any, menuId: string): void {
  if (sub.id !== menuId) out.push(sub);
}

function removeErrorText(err: any): string {
  if (isConflict(err)) return 'El menú tiene submenús asociados';
  return fallbackRemoveText(err);
}

function isConflict(err: any): boolean {
  return err?.status === 409;
}

function fallbackRemoveText(err: any): string {
  return err?.error?.error || 'Error al eliminar menú';
}

function buildMenuOrderPayload(menus: any[]): any[] {
  const payload: any[] = [];
  for (let i = 0; i < menus.length; i++) pushMenuOrder(payload, menus[i], i);
  return payload;
}

function pushMenuOrder(payload: any[], menu: any, index: number): void {
  payload.push({ id: menu.id, index });
  const subs = menu.submenu || [];
  for (let j = 0; j < subs.length; j++) pushSubOrder(payload, subs[j], j);
}

function pushSubOrder(payload: any[], sub: any, index: number): void {
  payload.push({ id: sub.id, index });
}
